import { logger } from './logger';

const generateRandomString = (length: number): string => {
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const values = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(values).map(x => possible[x % possible.length]).join('');
};

const base64URLEncode = (str: string): string => {
  return btoa(str)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

const sha256 = async (plain: string): Promise<ArrayBuffer> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return crypto.subtle.digest('SHA-256', data);
};

const bufferToBase64URL = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return base64URLEncode(binary);
};

export const generatePKCECodes = async (): Promise<{ verifier: string; challenge: string }> => {
  try {
    const verifier = generateRandomString(64);
    const challengeBuffer = await sha256(verifier);
    const challenge = bufferToBase64URL(challengeBuffer);

    logger.debug('PKCE', 'Generated PKCE codes', {
      verifier: verifier.slice(0, 10) + '...',
      challenge: challenge.slice(0, 10) + '...',
    });

    return { verifier, challenge };
  } catch (error) {
    logger.error('PKCE', 'Failed to generate PKCE codes', { error });
    throw error;
  }
};

export const getAuthorizationUrl = (codeChallenge: string): string => {
  const callbackUrl = import.meta.env.VITE_DEV_MODE === 'true'
    ? import.meta.env.VITE_AUTH_CALLBACK_URL_DEV
    : import.meta.env.VITE_AUTH_CALLBACK_URL_PROD;

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: import.meta.env.VITE_WSO2_CLIENT_ID,
    redirect_uri: callbackUrl,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    scope: 'openid profile email internal_user_mgt_list internal_user_mgt_view internal_user_mgt_create internal_user_mgt_update internal_user_mgt_delete',
  });

  const authUrl = `${import.meta.env.VITE_AUTH_URL}/oauth2/authorize?${params}`;
  logger.debug('PKCE', 'Generated authorization URL', { 
    authUrl,
    redirectUri: callbackUrl,
    devMode: import.meta.env.VITE_DEV_MODE
  });

  return authUrl;
};
